import fontkit from '@pdf-lib/fontkit';
import moment from 'moment';
import { PDFDocument, PDFPage, rgb, StandardFonts } from 'pdf-lib';

import { Referrer, User, utils } from '../../core';

export enum Template {
  physician = 'physician',
  traditional = 'traditional',
  consent = 'patient-consent',
  invoice = 'invoice',
  invoiceReferrer = 'detailed-invoice',
  medicalForm = 'medical-history-form',
  prescription = 'prescription',
  riskReport = 'risk-report',
  test = 'test',
}

export interface PDFModifierProps {
  templateName: Template | string;
  fields?: any;
  user?: User;
  referrer?: Referrer;
  skuId?: string;
  studyId?: string;
}

export interface Field {
  page: number;
  x: number;
  y: number;
  font?: string;
  size?: number;
  scale?: number;
}

// this is necessary for gatsby to build using the window object
const isBrowser = typeof window !== 'undefined';
// @ts-ignore
const { host } = isBrowser && window.location;
const environment = host?.includes('staging.prenuvo')
  ? '-staging'
  : host?.includes('www.prenuvo')
  ? ''
  : '-dev';

class PDFModifier {
  user: User | null;
  referrer?: Referrer | null;
  skuId?: string | null;
  studyId?: string | null;
  fields?: any | null;
  templateName: Template | string;

  constructor({ user, referrer, skuId, studyId, templateName, fields }: PDFModifierProps) {
    this.user = user || null;
    this.referrer = referrer || null;
    this.skuId = skuId || null;
    this.studyId = studyId;
    this.fields = fields || new Map();
    this.templateName = templateName;
  }

  /**
   * Fetching pdf and converting to bytes
   * @param url string
   * @returns base64 string
   */
  public async fetchPdf(url: string) {
    try {
      const existingPdfBytes = await this.convertToBuffer(url);
      return await this.modifyPdf(existingPdfBytes);
    } catch (e) {
      throw e;
    }
  }

  /**
   * Download pdf file
   * @param pdf base64 string
   * @param pdfName string
   */
  public download(pdf: string, pdfName?: string) {
    const link = document.createElement('a');
    link.href = pdf;
    if (pdfName) {
      link.download = pdfName;
    } else {
      link.download = `${this.templateName}-${this.studyId}.pdf`;
    }
    link.click();
  }

  /**
   * Download pdf file
   * @param pdf presigned url
   * @param pdfName string
   */
  public downloadPdfFromUrl(pdfUrl: string, pdfName?: string) {
    fetch(pdfUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.target = '_blank';
        if (pdfName) {
          link.download = pdfName;
        } else {
          link.download = `${this.templateName}-${this.studyId}.pdf`;
        }
        link.click();
      });
  }

  handlePatientHeaderInformation(
    pages: PDFPage[],
    key: string,
    field: Field,
    font: string | undefined,
    pdfFont: any,
    size: number | undefined
  ) {
    for (const pg in pages) {
      // clear existing auth0 id
      pages[pg].drawRectangle({ x: 350, y: 809, width: 250, height: 20, color: rgb(1, 1, 1) });
      // draw full name
      pages[pg].drawText(<string>`Patient: ${this.getTemplateValues(key)}`, {
        x: this.getXPosition(key, 375, field, font),
        y: this.getYPosition(key, 820),
        color: this.getColor(),
        font: pdfFont,
        size,
      });
    }
  }

  /**
   * Modify pdf content using fields from template
   * @param bytes array buffer bytes or base64 string
   * @returns base64 string
   */
  public async modifyPdf(bytes: ArrayBuffer | string) {
    try {
      const pdfDoc = await PDFDocument.load(bytes);

      const pages = pdfDoc.getPages();

      for (const [key, value] of this.fields) {
        await Promise.all(
          value.map(async (field: Field) => {
            try {
              const { x, y, size, page, font, scale } = field;
              if (key === 'referrerSignature' && this?.referrer?.signature) {
                const signatureImage = await pdfDoc.embedPng(this.referrer.signature);
                const imageDimensions = signatureImage.scale(scale || 1);

                const isPrescription = this.templateName === Template.prescription;

                pages[page].drawImage(signatureImage, {
                  x: isPrescription ? this.getCenteredXPosition(x, imageDimensions.width) : x,
                  y: y,
                  width: imageDimensions.width,
                  height: imageDimensions.height,
                });
              }

              if (
                key === 'fullname' &&
                ['physician', 'structured'].some((reportName) =>
                  this.templateName.includes(reportName)
                )
              ) {
                const pdfFont = await this.fetchPdfFont('Helvetica', pdfDoc);
                this.handlePatientHeaderInformation(pages, key, field, font, pdfFont, size);
              }

              if (font) {
                const customFont = await this.fetchPdfFont(font, pdfDoc);

                pages[page].drawText(<string>this.getTemplateValues(key), {
                  x: this.getXPosition(key, x, field, customFont),
                  y: this.getYPosition(key, y),
                  color: this.getColor(),
                  font: customFont,
                  size,
                });
              }
            } catch (err) {
              throw err;
            }
          })
        );
      }

      const pdfBytes = await pdfDoc.saveAsBase64({ dataUri: true });
      return pdfBytes;
    } catch (e) {
      throw new Error((e as Error).message || 'Something went wrong');
    }
  }

  private async convertToBuffer(url: string) {
    try {
      const existingPdfBytes = await fetch(url, { credentials: 'include' }).then((response) =>
        response.arrayBuffer()
      );
      const bytes = new Uint8Array(existingPdfBytes);
      return bytes;
    } catch (e) {
      throw new Error((e as Error).message || 'Something went wrong');
    }
  }

  /**
   * Download any custom font for pdf template
   */
  private async fetchPdfFont(font: string, pdfDoc: any) {
    if (font === 'Quicksand') {
      const url = `https://prenuvo-apps-cloudfront-assets${environment}.s3.ca-central-1.amazonaws.com/fonts/Quicksand-Regular.ttf`;
      const fontBytes = await fetch(url).then((res) => res.arrayBuffer());
      await pdfDoc.registerFontkit(fontkit);
      const pdfFont = await pdfDoc.embedFont(fontBytes);
      return pdfFont;
    }
    //@ts-ignore
    const pdfFont = await pdfDoc.embedFont(StandardFonts[font]);

    return pdfFont;
  }

  /**
   * Get string values by template keys for modified pdf document
   */
  private getTemplateValues(key: string) {
    if (this.user) {
      const {
        firstname,
        lastname,
        gender,
        dob,
        fakeFirstname,
        fakeLastname,
        fakeDob,
        phn,
        phoneNumber,
      } = this.user;
      const isPrescription = this.templateName === Template.prescription;

      switch (key) {
        case 'fullname':
          return `${firstname} ${lastname}`;
        case 'gender':
          return utils.capitalize(gender || '');
        case 'dob':
          if (isPrescription) {
            return dob ? moment(dob).format('DD MMMM YYYY') : '';
          }
          return dob;
        case 'msp_prescriber':
          return this.referrer ? `${this.referrer.firstName} ${this.referrer.lastName}` : '';
        case 'fake_fullname':
          return `${fakeFirstname} ${fakeLastname} (Alias)`;
        case 'fake_dob':
          return `${fakeDob} (Approx.)`;
        case 'identifier':
          return this.studyId ? this.studyId.split('-').join('').substr(0, 16) : '';
        case 'phn':
          return phn || '';
        case 'msp_number':
          return this?.referrer?.mspNumber || '';
        case 'phoneNumber':
          return phoneNumber;
        default:
          return '';
      }
    }

    return '';
  }

  /**
   * Get custom CENTERED 'x' position by special template keys for modified pdf document
   */
  private getCenteredXPosition(maximum: number, textWidth: number) {
    const textWidthModifier = 1.05 / 2;
    return maximum / 2 - Math.round(textWidthModifier * textWidth);
  }

  /**
   * Get custom RIGHT ALIGNED 'x' position by special template keys for modified pdf document
   */
  private getRightAlignedXPosition(maximum: number, textWidth: number) {
    const textWidthModifier = 1.05;
    return maximum - Math.round(textWidthModifier * textWidth);
  }

  /**
   * Get custom 'x' position by special template keys for modified pdf document
   */
  private getXPosition(key: string, position: number, field: Field, customFont: any) {
    const isInvoice = this.templateName === Template.invoice;
    const isInvoiceReferrer = this.templateName === Template.invoiceReferrer;
    const isPrescription = this.templateName === Template.prescription;

    switch (key) {
      case 'fullname':
      case 'msp_prescriber':
      case 'msp_number':
        if (isInvoice || isInvoiceReferrer || isPrescription) {
          if (isPrescription && key === 'fullname') {
            return position;
          }
          const userFullName = this.getTemplateValues(key);
          const textWidth = customFont.widthOfTextAtSize(userFullName, field.size);

          if (isInvoiceReferrer) {
            return this.getRightAlignedXPosition(556, textWidth);
          }
          if (isPrescription) {
            return this.getCenteredXPosition(570, textWidth);
          }
          return field.x;
        } else {
          return position;
        }
      default:
        return position;
    }
  }

  /**
   * Get custom 'y' position by special template keys for modified pdf document
   */
  private getYPosition(key: string, position: number) {
    const isPatientScan = !['400', '350', '370'].includes(this.skuId ?? '');
    const isInvoiceReferrer = this.templateName === Template.invoiceReferrer;

    switch (key) {
      case 'gender':
        return isPatientScan ? position + 13 : position;
      case 'dob':
        return isPatientScan ? position + 13 : position;
      case 'fullname':
        return isInvoiceReferrer ? position + 6 : position;
      default:
        return position;
    }
  }

  /**
   * Get custom color for all fileds in pdf
   */
  private getColor() {
    switch (this.templateName) {
      case Template.invoice:
      case Template.invoiceReferrer:
        return rgb(0.29, 0.29, 0.29);
      default:
        return rgb(0, 0, 0);
    }
  }
}

export default PDFModifier;
